import { useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { loadReference } from '../../lib/reference';
import { STATE_LABEL, useNova } from '../../lib/store';
import { Button, StateDot } from '../ui';
import { AddButton, Cell, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, tempPassword, Toggle, useLoad, when } from './kit';

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: Role;
  extension: string | null;
  active: boolean;
  last_login_at: string | null;
  team_id: string | null;
  team: string | null;
  skills: { skillId: string; skill: string; level: number }[];
}
type Role = 'agent' | 'supervisor' | 'qa' | 'admin' | 'manager';
interface Named {
  id: string;
  name: string;
}

const ROLES: { id: Role; label: string; hint: string }[] = [
  { id: 'agent', label: 'Agent', hint: 'Takes calls' },
  { id: 'supervisor', label: 'Supervisor', hint: 'Takes calls and runs live operations' },
  { id: 'qa', label: 'Quality analyst', hint: 'Reviews calls and recordings' },
  { id: 'manager', label: 'Manager', hint: 'Live operations and history' },
  { id: 'admin', label: 'Admin', hint: 'Everything, including this console' },
];
const roleLabel = (r: Role) => ROLES.find((x) => x.id === r)?.label ?? r;

export function Users() {
  const [users, reload] = useLoad<UserRow[]>('/users');
  const [teams] = useLoad<Named[]>('/teams');
  const [skills] = useLoad<Named[]>('/skills');
  const agents = useNova((s) => s.agents);
  const me = useNova((s) => s.user?.sub);
  const [editing, setEditing] = useState<UserRow | 'new' | null>(null);

  const done = () => {
    setEditing(null);
    reload();
    void loadReference();
  };

  return (
    <>
      <SectionHeader
        title="Users"
        lead="Everyone who signs in to Nova. Give a user an extension and they get a browser softphone; skills decide which queues ring them."
        action={<AddButton onClick={() => setEditing('new')}>Add user</AddButton>}
      />
      <Table head={['Name', 'Role', 'Extension', 'Skills', 'Now', 'Last sign-in']} empty={users?.length === 0 && 'No users yet.'}>
        {(users ?? []).map((u) => {
          const live = agents[u.id];
          return (
            <Row key={u.id} onClick={() => setEditing(u)} muted={!u.active}>
              <Cell>
                <span className="block font-medium">{u.name}</span>
                <span className="text-xs text-ink-3">{u.email}</span>
              </Cell>
              <Cell>
                {roleLabel(u.role)}
                {u.team ? <span className="block text-xs text-ink-3">{u.team}</span> : null}
              </Cell>
              <Cell className="tabular">{u.extension ?? <span className="text-ink-3">None</span>}</Cell>
              <Cell>
                <span className="flex flex-wrap gap-1">
                  {u.skills.length ? u.skills.map((s) => <Pill key={s.skillId}>{`${s.skill} ${s.level}`}</Pill>) : <span className="text-ink-3">None</span>}
                </span>
              </Cell>
              <Cell>
                {!u.active ? (
                  <Pill tone="bad">Deactivated</Pill>
                ) : live ? (
                  <span className="inline-flex items-center gap-1.5">
                    <StateDot state={live.state} size={8} /> {STATE_LABEL[live.state]}
                  </span>
                ) : (
                  <span className="text-ink-3">Offline</span>
                )}
              </Cell>
              <Cell className="text-ink-2">{when(u.last_login_at)}</Cell>
            </Row>
          );
        })}
      </Table>
      {editing ? (
        <UserDrawer
          user={editing === 'new' ? null : editing}
          isMe={editing !== 'new' && editing.id === me}
          teams={teams ?? []}
          skills={skills ?? []}
          onClose={() => setEditing(null)}
          onDone={done}
        />
      ) : null}
    </>
  );
}

function UserDrawer({
  user,
  isMe,
  teams,
  skills,
  onClose,
  onDone,
}: {
  user: UserRow | null;
  isMe: boolean;
  teams: Named[];
  skills: Named[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'agent');
  const [teamId, setTeamId] = useState(user?.team_id ?? '');
  const [extension, setExtension] = useState(user?.extension ?? '');
  const [levels, setLevels] = useState<Record<string, number>>(Object.fromEntries((user?.skills ?? []).map((s) => [s.skillId, s.level])));
  const [password, setPassword] = useState(user ? '' : tempPassword());
  const [busy, setBusy] = useState(false);
  const [shownPassword, setShownPassword] = useState<string | null>(null);

  const skillList = Object.entries(levels).map(([skillId, level]) => ({ skillId, level }));

  async function save() {
    setBusy(true);
    if (!user) {
      const ok = await act(
        () =>
          api('POST', '/users', {
            name,
            email,
            password,
            role,
            extension: extension || undefined,
            teamId: teamId || undefined,
            skills: skillList,
          }),
        `${name} added`,
      );
      setBusy(false);
      if (ok) setShownPassword(password);
      return;
    }
    const ok = await act(async () => {
      await api('PATCH', `/users/${user.id}`, {
        name,
        role: isMe ? undefined : role,
        teamId: teamId || null,
        extension: !user.extension && extension ? extension : undefined,
      });
      await api('PATCH', `/users/${user.id}/skills`, { skills: skillList });
      return true;
    }, 'Changes saved');
    setBusy(false);
    if (ok) onDone();
  }

  async function resetPassword() {
    const pw = tempPassword();
    const ok = await act(() => api('POST', `/users/${user!.id}/password`, { password: pw }).then(() => true));
    if (ok) setShownPassword(pw);
  }

  async function setActive(active: boolean) {
    const ok = await act(() => api('PATCH', `/users/${user!.id}`, { active }), active ? `${user!.name} reactivated` : `${user!.name} deactivated and signed out`);
    if (ok) onDone();
  }

  if (shownPassword) {
    return (
      <Drawer
        title="Share the sign-in details"
        open
        onClose={onDone}
        footer={
          <Button tone="primary" onClick={onDone}>
            Done
          </Button>
        }
      >
        <p className="mb-4 text-sm text-ink-2">Give these to {name}. The password is shown only now; ask them to keep it private.</p>
        <dl className="rounded-xl border border-line p-4 text-sm">
          <dt className="text-ink-3">Email</dt>
          <dd className="mb-3 font-medium">{email}</dd>
          <dt className="text-ink-3">Password</dt>
          <dd className="tabular text-lg">{shownPassword}</dd>
        </dl>
        <Button className="mt-4" onClick={() => void navigator.clipboard?.writeText(`${email}\n${shownPassword}`)}>
          Copy both
        </Button>
      </Drawer>
    );
  }

  const valid = name.trim().length >= 2 && (user || (/^\S+@\S+\.\S+$/.test(email) && password.length >= 10)) && (!extension || /^\d{3,6}$/.test(extension));

  return (
    <Drawer
      title={user ? `Edit ${user.name}` : 'Add a user'}
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Saving…' : user ? 'Save changes' : 'Add user'}
          </Button>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <Field label="Full name">{(id) => <input id={id} className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
      <Field label="Work email" hint={user ? 'The sign-in email cannot be changed.' : undefined}>
        {(id) => <input id={id} type="email" className={inputClass} value={email} disabled={Boolean(user)} onChange={(e) => setEmail(e.target.value)} />}
      </Field>
      <Field label="Role" hint={isMe ? 'You cannot change your own role.' : ROLES.find((r) => r.id === role)?.hint}>
        {(id) => (
          <select id={id} className={inputClass} value={role} disabled={isMe} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Team">
        {(id) => (
          <select id={id} className={inputClass} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">No team</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field
        label="Extension"
        hint={user?.extension ? 'Extensions cannot be changed once set.' : '3 to 6 digits. Needed to take calls; leave empty for admins who never do.'}
      >
        {(id) => (
          <input
            id={id}
            inputMode="numeric"
            className={`${inputClass} tabular`}
            value={extension}
            disabled={Boolean(user?.extension)}
            onChange={(e) => setExtension(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        )}
      </Field>

      <p className="mb-2 text-sm font-medium">Skills</p>
      <p className="mb-3 text-xs text-ink-3">Level 1 to 5. A queue rings people whose level meets its minimum for every skill it asks for.</p>
      <ul className="mb-5 flex flex-col gap-2">
        {skills.map((s) => {
          const level = levels[s.id];
          return (
            <li key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2">
              <label className="flex items-center gap-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={level !== undefined}
                  onChange={(e) =>
                    setLevels((l) => {
                      const next = { ...l };
                      if (e.target.checked) next[s.id] = 3;
                      else delete next[s.id];
                      return next;
                    })
                  }
                  className="h-4 w-4 accent-[var(--color-ion)]"
                />
                {s.name}
              </label>
              {level !== undefined ? (
                <div className="flex gap-1" role="radiogroup" aria-label={`${s.name} level`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={level === n}
                      onClick={() => setLevels((l) => ({ ...l, [s.id]: n }))}
                      className={`tabular h-7 w-7 rounded-md border text-xs ${level >= n ? 'border-ion bg-ion/15 text-ink' : 'border-line text-ink-3'}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
        {skills.length === 0 ? <li className="text-sm text-ink-3">No skills yet. Add them under Skills and teams.</li> : null}
      </ul>

      {!user ? (
        <Field label="Temporary password" hint="Shown once after saving so you can share it.">
          {(id) => (
            <div className="flex gap-2">
              <input id={id} className={`${inputClass} tabular`} value={password} onChange={(e) => setPassword(e.target.value)} />
              <Button className="px-3 py-2" onClick={() => setPassword(tempPassword())}>
                New
              </Button>
            </div>
          )}
        </Field>
      ) : (
        <div className="mt-2 flex flex-col gap-3 border-t border-line pt-4">
          <Button className="justify-start" onClick={() => void resetPassword()}>
            Reset password
          </Button>
          {!isMe ? (
            user.active ? (
              <Toggle checked label="Account active. Turning it off signs them out at once." onChange={() => void setActive(false)} />
            ) : (
              <Toggle checked={false} label="Account deactivated. Turn on to let them sign in again." onChange={() => void setActive(true)} />
            )
          ) : null}
        </div>
      )}
    </Drawer>
  );
}
